import {styled} from '@volkswagen-onehub/components-core'
import React, {useEffect, useRef, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {useDispatch} from 'react-redux'
import {setSelectedCarModelTypes} from '../../../../features/general/generalSlice'
import Option from '../../../../utils/filters/Option'
import getNewOptionValue from '../../../../utils/filters/getNewOptionValue'
import MultiSelectStatus from '../../ui/MultiSelectStatus'
import {sortOptionsAlphabetically} from '../../../../shared/utilities'
import {Params} from '../../../../shared/variables'
import {useFeatureServices} from '../../../../use-feature-services'
import {setUrlParams} from '../../../../utils/url/setUrlParams'
import ResponsiveMultiSelect from '../../ui/ResponsiveMultiSelect'

export interface FilterByCarModelProps {
    carModelOptions: Option
    selectedCarModel: string[]
    onClick: () => void
}

const LongMultiSelect = styled.div`
    fieldset > div:last-child {
        max-height: 390px;
        overflow-y: auto;
    }
`

export default function FilterByCarModel(
    props: FilterByCarModelProps
): React.JSX.Element {
    const {t} = useTranslation('app')
    const dispatch = useDispatch()
    const {'s2:history': historyService} = useFeatureServices() as any
    const [multiselectStatusMessage, setMultiselectStatusMessage] =
        useState<string>(
            t('filters.noneSelected', {
                title: t('filters.aria-labels.modelType'),
            })
        )

    const updateStatusFromValues = (values: string[]) => {
        const selectedLabels = values.map(
            // eslint-disable-next-line security/detect-object-injection
            (key) => props.carModelOptions[key] || key
        )

        setMultiselectStatusMessage(
            selectedLabels.length > 0
                ? t('filters.statusMessage', {
                      title: t('filters.aria-labels.modelType'),
                      values: selectedLabels.join(', '),
                  })
                : t('filters.noneSelected', {
                      title: t('filters.aria-labels.modelType'),
                  })
        )
    }

    const appliedFromUrlRef = useRef(false)
    useEffect(() => {
        if (appliedFromUrlRef.current) {
            return
        }
        if (
            !props.carModelOptions ||
            !Object.keys(props.carModelOptions).length
        ) {
            return
        }

        const url = new URL(window.location.href)
        const raw =
            url.searchParams.get(Params.MODELS) ??
            url.searchParams.get(`${Params.MODELS}-app`) ??
            ''

        if (!raw) {
            appliedFromUrlRef.current = true

            return
        }

        const urlValues = raw
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean)

        const validValues = urlValues.filter((v) =>
            Object.prototype.hasOwnProperty.call(props.carModelOptions, v)
        )

        if (validValues.length) {
            dispatch(setSelectedCarModelTypes(validValues))
            updateStatusFromValues(validValues)
        }

        appliedFromUrlRef.current = true
    }, [props.carModelOptions, dispatch])

    const handleOptionClick = (value: string) => {
        props.onClick()

        const newValue = getNewOptionValue(props.selectedCarModel, value)
        dispatch(setSelectedCarModelTypes(newValue))
        updateStatusFromValues(newValue)

        setUrlParams(
            historyService,
            Params.MODELS,
            newValue.length ? newValue.join(',') : ''
        )
    }

    return (
        <>
            <LongMultiSelect>
                <ResponsiveMultiSelect
                    name={'modelType'}
                    label={t('filters.modelType')}
                    value={props.selectedCarModel}
                    options={sortOptionsAlphabetically(props.carModelOptions)}
                    onOptionClick={handleOptionClick}
                />
            </LongMultiSelect>
            {multiselectStatusMessage && (
                <MultiSelectStatus message={multiselectStatusMessage} />
            )}
        </>
    )
}
